import {
  SdkObjectLockSchema,
  SdkScheduleItemSchema,
  SdkWorkspaceBookmarkSchema,
  SdkWorkspaceRecentSchema,
  SdkWorkSurfaceSubjectRefSchema,
  type SdkObjectLock,
  type SdkScheduleItem,
  type SdkShareView,
  type SdkSignal,
  type SdkTopicThread,
  type SdkWorkItem,
  type SdkWorkspaceBookmark,
  type SdkWorkspaceRecent,
  type SdkWorkSurfaceActorRef,
  type SdkWorkSurfaceSubjectRef,
} from '@boox/sdk-contracts';
import { api } from './api';

export interface WorkSurfaceValidationResult<T> {
  ok: boolean;
  value?: T;
  issues: string[];
}

export interface WorkSurfaceEntityAnchor {
  entityType: string;
  entityId: string;
}

export interface CreateWorkSurfaceWorkItemInput {
  subjectRef: SdkWorkSurfaceSubjectRef;
  kind?: SdkWorkItem['kind'];
  title: string;
  description?: string;
  priority?: 'P0' | 'P1' | 'P2';
  actionRef?: string;
  sourceRef?: string;
  assignedTo?: string;
  proposedPayload?: Record<string, unknown>;
  evidence?: Record<string, unknown>;
}

export interface RequestWorkSurfaceApprovalInput {
  requestType?: string;
  requesterRole?: string;
  reviewerRole?: string;
  title?: string;
  description?: string;
  priority?: 'P0' | 'P1' | 'P2';
  timeoutMinutes?: number;
  escalationRole?: string;
}

export interface RaiseWorkSurfaceSignalInput {
  subjectRef: SdkWorkSurfaceSubjectRef;
  signalType?: SdkSignal['signalType'];
  title: string;
  details?: string;
  category?: string;
  severity?: SdkSignal['severity'];
  site?: string;
  reporterName?: string;
  reporterRole?: string;
  attachments?: Array<Record<string, unknown>>;
}

export interface CreateWorkSurfaceTopicThreadInput {
  subjectRef: SdkWorkSurfaceSubjectRef;
  name?: string;
  description?: string;
  externalScope?: boolean;
  restrictedTo?: string[];
}

export interface PostWorkSurfaceTopicMessageInput {
  threadId: string;
  content: string;
  contentType?: 'text' | 'voice' | 'image' | 'file' | 'system';
  attachments?: Array<Record<string, unknown>>;
  isInternalNote?: boolean;
  parentId?: string | null;
}

export interface CreateWorkSurfaceShareViewInput {
  subjectRef: SdkWorkSurfaceSubjectRef;
  label?: string;
  expiresInDays?: number;
  maxViews?: number;
  visibleFieldPolicyRef?: string;
  allowedActionPolicyRef?: string;
  templateKey?: 'summary' | 'operations' | 'approval' | 'full' | 'custom';
  visibleFields?: string[];
  action?: Record<string, unknown>;
}

export interface CreateWorkspaceBookmarkInput {
  title: string;
  targetType: SdkWorkspaceBookmark['targetType'];
  moduleRef?: string;
  route?: string;
  subjectRef?: SdkWorkSurfaceSubjectRef;
  viewRef?: string;
  actionRef?: string;
  icon?: string;
  color?: string;
  group?: string;
  sortOrder?: number;
  isPinned?: boolean;
  openMode?: SdkWorkspaceBookmark['openMode'];
  metadata?: Record<string, unknown>;
}

export interface UpdateWorkspaceBookmarkInput extends Partial<CreateWorkspaceBookmarkInput> {}

export interface CreateWorkspaceRecentInput {
  title: string;
  dedupeKey?: string;
  moduleRef?: string;
  route?: string;
  subjectRef?: SdkWorkSurfaceSubjectRef;
  icon?: string;
  color?: string;
  metadata?: Record<string, unknown>;
}

export interface OmWorkspaceQueueItem {
  id: string;
  title: string;
  subtitle?: string;
  route?: string;
  moduleRef?: string;
  status?: string;
  priority?: string;
  severity?: string;
  dueAt?: string;
  createdAt?: string;
  targetType?: SdkWorkspaceBookmark['targetType'];
  subjectRef?: SdkWorkSurfaceSubjectRef;
  metadata?: Record<string, unknown>;
}

export interface OmWorkspaceRoleProfile {
  key: string;
  title: string;
  summary: string;
  focusAreas: string[];
  workLabel: string;
  signalLabel: string;
  teamWorkLabel?: string;
  teamSignalLabel?: string;
}

export interface OmWorkspaceTodayData {
  workspaceMode?: 'personal' | 'management';
  roleProfile?: OmWorkspaceRoleProfile;
  stats: {
    activeWorkCount: number;
    teamWorkCount?: number;
    signalCount: number;
    teamSignalCount?: number;
    dueCount: number;
    bookmarkCount: number;
  };
  workItems: OmWorkspaceQueueItem[];
  teamWorkItems?: OmWorkspaceQueueItem[];
  signals: OmWorkspaceQueueItem[];
  teamSignals?: OmWorkspaceQueueItem[];
  dueTasks: OmWorkspaceQueueItem[];
  bookmarks: SdkWorkspaceBookmark[];
  recents: SdkWorkspaceRecent[];
}

export interface OmWorkspaceSearchItem {
  id: string;
  kind: 'bookmark' | 'saved_view' | 'recent' | 'work_item' | 'signal' | 'entity';
  title: string;
  subtitle?: string;
  route?: string;
  moduleRef?: string;
  icon?: string;
  openMode?: SdkWorkspaceBookmark['openMode'];
  targetType?: SdkWorkspaceBookmark['targetType'] | 'recent';
  subjectRef?: SdkWorkSurfaceSubjectRef;
  metadata?: Record<string, unknown>;
}

export interface OmWorkspaceSearchData {
  query: string;
  items: OmWorkspaceSearchItem[];
  counts: {
    bookmarks: number;
    savedViews?: number;
    recents: number;
    workItems: number;
    signals: number;
    entities: number;
  };
}

export interface OmEntityCockpitTimelineItem {
  id: string;
  kind: 'work_item' | 'signal' | 'recent' | 'bookmark';
  title: string;
  subtitle?: string;
  at: string;
  route?: string;
}

export interface OmEntityCockpitData {
  subjectRef: SdkWorkSurfaceSubjectRef;
  bookmark: SdkWorkspaceBookmark | null;
  relatedBookmarks: SdkWorkspaceBookmark[];
  recents: SdkWorkspaceRecent[];
  workItems: OmWorkspaceQueueItem[];
  signals: OmWorkspaceQueueItem[];
  timeline: OmEntityCockpitTimelineItem[];
  stats: {
    workItemCount: number;
    signalCount: number;
    recentCount: number;
    bookmarkCount: number;
  };
}

export type WorkSurfaceApiRecord = Record<string, unknown>;

function zodIssues(error: { issues: Array<{ path: Array<string | number>; message: string }> }): string[] {
  return error.issues.map((issue) => `${issue.path.join('.') || '<root>'}: ${issue.message}`);
}

export function validateWorkSurfaceSubjectRef(input: unknown): WorkSurfaceValidationResult<SdkWorkSurfaceSubjectRef> {
  const parsed = SdkWorkSurfaceSubjectRefSchema.safeParse(input);
  if (parsed.success) return { ok: true, value: parsed.data, issues: [] };
  return { ok: false, issues: zodIssues(parsed.error) };
}

export function getWorkSurfaceEntityAnchor(subjectRef: SdkWorkSurfaceSubjectRef): WorkSurfaceEntityAnchor {
  if (!subjectRef.entityType || !subjectRef.entityId) {
    throw new Error('Work surface operation requires subjectRef.entityType and subjectRef.entityId.');
  }
  return {
    entityType: subjectRef.entityType,
    entityId: subjectRef.entityId,
  };
}

function entitySlugForSubject(subjectRef?: Pick<SdkWorkSurfaceSubjectRef, 'entityType' | 'entityId'>): string | null {
  const entityType = subjectRef?.entityType?.trim().toLowerCase();
  if (!entityType || !subjectRef?.entityId) return null;
  if (entityType === 'job' || entityType === 'jobs') return 'jobs';
  if (entityType === 'shipment' || entityType === 'shipments') return 'shipments';
  if (entityType === 'customer' || entityType === 'customers') return 'customers';
  if (entityType === 'container' || entityType === 'containers') return 'containers';
  if (entityType === 'inventory_item' || entityType === 'inventory_items' || entityType === 'inventory-item' || entityType === 'inventory-items') return 'inventory-items';
  return entityType.endsWith('s') ? entityType : `${entityType}s`;
}

export function resolveWorkSurfaceRoute(input: {
  route?: string;
  subjectRef?: Pick<SdkWorkSurfaceSubjectRef, 'entityType' | 'entityId'>;
}): string | null {
  if (input.route && input.route.length > 0) return input.route;
  const slug = entitySlugForSubject(input.subjectRef);
  if (!slug || !input.subjectRef?.entityId) return null;
  return `/e/${slug}/${input.subjectRef.entityId}`;
}

export function buildWorkSurfaceObjectLockDraft(input: {
  lockId: string;
  subjectRef: SdkWorkSurfaceSubjectRef;
  lockType: SdkObjectLock['lockType'];
  ownerRef: SdkWorkSurfaceActorRef;
  reason: string;
  acquiredAt?: string;
  expiresAt?: string;
  runId?: string;
  versionTag?: SdkObjectLock['versionTag'];
}): SdkObjectLock {
  return SdkObjectLockSchema.parse({
    lockId: input.lockId,
    subjectRef: input.subjectRef,
    lockType: input.lockType,
    status: 'locked',
    ownerRef: input.ownerRef,
    reason: input.reason,
    acquiredAt: input.acquiredAt ?? new Date().toISOString(),
    expiresAt: input.expiresAt,
    runId: input.runId,
    versionTag: input.versionTag ?? 'final',
  });
}

export function buildWorkSurfaceScheduleDraft(input: {
  scheduleItemId: string;
  subjectRef: SdkWorkSurfaceSubjectRef;
  scheduleType: SdkScheduleItem['scheduleType'];
  title: string;
  createdBy: SdkWorkSurfaceActorRef;
  startsAt: string;
  endsAt?: string;
  timezone?: string;
  description?: string;
  actionRef?: string;
  reminderPolicyRef?: string;
  recurrenceRef?: string;
}): SdkScheduleItem {
  return SdkScheduleItemSchema.parse({
    scheduleItemId: input.scheduleItemId,
    subjectRef: input.subjectRef,
    scheduleType: input.scheduleType,
    title: input.title,
    description: input.description,
    actionRef: input.actionRef,
    createdBy: input.createdBy,
    startsAt: input.startsAt,
    endsAt: input.endsAt,
    timezone: input.timezone ?? 'UTC',
    reminderPolicyRef: input.reminderPolicyRef,
    recurrenceRef: input.recurrenceRef,
    createdAt: new Date().toISOString(),
  });
}

function mapPriority(priority?: 'P0' | 'P1' | 'P2'): 'P0' | 'P1' | 'P2' {
  return priority ?? 'P1';
}

export const workSurfaceSdk = {
  validateSubjectRef: validateWorkSurfaceSubjectRef,
  getEntityAnchor: getWorkSurfaceEntityAnchor,
  buildObjectLockDraft: buildWorkSurfaceObjectLockDraft,
  buildScheduleDraft: buildWorkSurfaceScheduleDraft,
  resolveRoute: resolveWorkSurfaceRoute,

  createWorkItem(input: CreateWorkSurfaceWorkItemInput): Promise<WorkSurfaceApiRecord> {
    const subject = SdkWorkSurfaceSubjectRefSchema.parse(input.subjectRef);
    return api.post<WorkSurfaceApiRecord>('/work-surface/work-items', {
      subjectRef: subject,
      kind: input.kind ?? 'task',
      title: input.title,
      description: input.description,
      actionRef: input.actionRef,
      sourceRef: input.sourceRef ?? subject.dataRef ?? subject.runRef?.runId,
      priority: mapPriority(input.priority),
      assignedTo: input.assignedTo,
      proposedPayload: input.proposedPayload ?? {},
      evidence: input.evidence ?? {},
    });
  },

  requestApproval(workItemId: string, input: RequestWorkSurfaceApprovalInput = {}): Promise<WorkSurfaceApiRecord> {
    return api.post<WorkSurfaceApiRecord>(`/work-surface/work-items/${encodeURIComponent(workItemId)}/request-approval`, input);
  },

  raiseSignal(input: RaiseWorkSurfaceSignalInput): Promise<WorkSurfaceApiRecord> {
    const subject = SdkWorkSurfaceSubjectRefSchema.parse(input.subjectRef);
    return api.post<WorkSurfaceApiRecord>('/work-surface/signals', {
      subjectRef: subject,
      signalType: input.signalType ?? 'operational-signal',
      category: input.category ?? input.signalType ?? 'Suggestion',
      severity: input.severity ?? 'medium',
      site: input.site ?? subject.moduleRef ?? subject.scope,
      reporterName: input.reporterName,
      reporterRole: input.reporterRole,
      title: input.title,
      details: input.details,
      attachments: input.attachments,
    });
  },

  createTopicThread(input: CreateWorkSurfaceTopicThreadInput): Promise<SdkTopicThread | WorkSurfaceApiRecord> {
    const subject = SdkWorkSurfaceSubjectRefSchema.parse(input.subjectRef);
    return api.post<SdkTopicThread | WorkSurfaceApiRecord>(
      '/work-surface/topic-threads',
      {
        subjectRef: subject,
        name: input.name,
        description: input.description,
        externalScope: input.externalScope,
        restrictedTo: input.restrictedTo,
      },
    );
  },

  postTopicMessage(input: PostWorkSurfaceTopicMessageInput): Promise<WorkSurfaceApiRecord> {
    return api.post<WorkSurfaceApiRecord>(`/work-surface/topic-threads/${encodeURIComponent(input.threadId)}/messages`, {
      content: input.content,
      contentType: input.contentType ?? 'text',
      attachments: input.attachments,
      isInternalNote: input.isInternalNote,
      parentId: input.parentId,
    });
  },

  createShareView(input: CreateWorkSurfaceShareViewInput): Promise<SdkShareView | WorkSurfaceApiRecord> {
    const subject = SdkWorkSurfaceSubjectRefSchema.parse(input.subjectRef);
    return api.post<SdkShareView | WorkSurfaceApiRecord>('/work-surface/share-views', {
      subjectRef: subject,
      label: input.label,
      expiresInDays: input.expiresInDays,
      maxViews: input.maxViews,
      visibleFieldPolicyRef: input.visibleFieldPolicyRef,
      allowedActionPolicyRef: input.allowedActionPolicyRef,
      templateKey: input.templateKey,
      visibleFields: input.visibleFields,
      action: input.action,
    });
  },

  acquireLock(input: {
    subjectRef: SdkWorkSurfaceSubjectRef;
    lockType: SdkObjectLock['lockType'];
    ownerRef?: SdkWorkSurfaceActorRef;
    reason: string;
    expiresAt?: string;
    runId?: string;
    versionTag?: SdkObjectLock['versionTag'];
  }): Promise<SdkObjectLock | WorkSurfaceApiRecord> {
    return api.post<SdkObjectLock | WorkSurfaceApiRecord>('/work-surface/object-locks', {
      subjectRef: input.subjectRef,
      lockType: input.lockType,
      ownerRef: input.ownerRef,
      reason: input.reason,
      expiresAt: input.expiresAt,
      runId: input.runId,
      versionTag: input.versionTag ?? 'final',
    });
  },

  releaseLock(lockId: string, reason?: string): Promise<SdkObjectLock | WorkSurfaceApiRecord> {
    return api.post<SdkObjectLock | WorkSurfaceApiRecord>(`/work-surface/object-locks/${encodeURIComponent(lockId)}/release`, {
      reason,
    });
  },

  createScheduleItem(input: {
    subjectRef: SdkWorkSurfaceSubjectRef;
    scheduleType?: SdkScheduleItem['scheduleType'];
    title: string;
    description?: string;
    startsAt: string;
    endsAt?: string;
    timezone?: string;
    actionRef?: string;
    recurrenceCron?: string;
    reminderPolicyRef?: string;
    idempotencyKey?: string;
  }): Promise<SdkScheduleItem | WorkSurfaceApiRecord> {
    return api.post<SdkScheduleItem | WorkSurfaceApiRecord>('/work-surface/schedule-items', {
      subjectRef: input.subjectRef,
      scheduleType: input.scheduleType ?? 'reminder',
      title: input.title,
      description: input.description,
      startsAt: input.startsAt,
      endsAt: input.endsAt,
      timezone: input.timezone,
      actionRef: input.actionRef,
      recurrenceCron: input.recurrenceCron,
      reminderPolicyRef: input.reminderPolicyRef,
      idempotencyKey: input.idempotencyKey,
    });
  },

  listBookmarks(query?: {
    pinned?: boolean;
    group?: string;
  }): Promise<SdkWorkspaceBookmark[]> {
    return api.get<SdkWorkspaceBookmark[]>('/work-surface/bookmarks', query);
  },

  createBookmark(input: CreateWorkspaceBookmarkInput): Promise<SdkWorkspaceBookmark> {
    return api.post<SdkWorkspaceBookmark>('/work-surface/bookmarks', {
      ...input,
      subjectRef: input.subjectRef ? SdkWorkSurfaceSubjectRefSchema.parse(input.subjectRef) : undefined,
    }).then((bookmark) => SdkWorkspaceBookmarkSchema.parse(bookmark));
  },

  updateBookmark(bookmarkId: string, input: UpdateWorkspaceBookmarkInput): Promise<SdkWorkspaceBookmark> {
    return api.patch<SdkWorkspaceBookmark>(`/work-surface/bookmarks/${encodeURIComponent(bookmarkId)}`, {
      ...input,
      subjectRef: input.subjectRef ? SdkWorkSurfaceSubjectRefSchema.parse(input.subjectRef) : undefined,
    }).then((bookmark) => SdkWorkspaceBookmarkSchema.parse(bookmark));
  },

  deleteBookmark(bookmarkId: string): Promise<{ deleted: true }> {
    return api.delete<{ deleted: true }>(`/work-surface/bookmarks/${encodeURIComponent(bookmarkId)}`);
  },

  openBookmark(bookmarkId: string): Promise<{ bookmark: SdkWorkspaceBookmark; recent: SdkWorkspaceRecent | null }> {
    return api.post<{ bookmark: SdkWorkspaceBookmark; recent: SdkWorkspaceRecent | null }>(
      `/work-surface/bookmarks/${encodeURIComponent(bookmarkId)}/open`,
      {},
    ).then((result) => ({
      bookmark: SdkWorkspaceBookmarkSchema.parse(result.bookmark),
      recent: result.recent ? SdkWorkspaceRecentSchema.parse(result.recent) : null,
    }));
  },

  reorderBookmarks(ids: string[]): Promise<SdkWorkspaceBookmark[]> {
    return api.post<SdkWorkspaceBookmark[]>('/work-surface/bookmarks/reorder', { ids })
      .then((rows) => rows.map((row) => SdkWorkspaceBookmarkSchema.parse(row)));
  },

  listRecents(limit = 12): Promise<SdkWorkspaceRecent[]> {
    return api.get<SdkWorkspaceRecent[]>('/work-surface/recents', { limit })
      .then((rows) => rows.map((row) => SdkWorkspaceRecentSchema.parse(row)));
  },

  trackRecent(input: CreateWorkspaceRecentInput): Promise<SdkWorkspaceRecent> {
    return api.post<SdkWorkspaceRecent>('/work-surface/recents', {
      ...input,
      subjectRef: input.subjectRef ? SdkWorkSurfaceSubjectRefSchema.parse(input.subjectRef) : undefined,
    }).then((recent) => SdkWorkspaceRecentSchema.parse(recent));
  },

  searchWorkspace(query = '', limit = 10): Promise<OmWorkspaceSearchData> {
    return api.get<OmWorkspaceSearchData>('/work-surface/search', { q: query, limit }).then((result) => ({
      ...result,
      items: Array.isArray(result.items) ? result.items.map((item) => ({
        ...item,
        route: resolveWorkSurfaceRoute({ route: item.route, subjectRef: item.subjectRef }) ?? undefined,
      })) : [],
    }));
  },

  getObjectCockpit(input: {
    subjectRef: SdkWorkSurfaceSubjectRef;
  }): Promise<OmEntityCockpitData> {
    const subject = SdkWorkSurfaceSubjectRefSchema.parse(input.subjectRef);
    if (!subject.entityType || !subject.entityId) {
      throw new Error('Object cockpit requires subjectRef.entityType and subjectRef.entityId.');
    }
    return api.get<OmEntityCockpitData>('/work-surface/cockpit', {
      entityType: subject.entityType,
      entityId: subject.entityId,
      moduleRef: subject.moduleRef,
    }).then((result) => ({
      ...result,
      bookmark: result.bookmark ? SdkWorkspaceBookmarkSchema.parse(result.bookmark) : null,
      relatedBookmarks: Array.isArray(result.relatedBookmarks) ? result.relatedBookmarks.map((row) => SdkWorkspaceBookmarkSchema.parse(row)) : [],
      recents: Array.isArray(result.recents) ? result.recents.map((row) => SdkWorkspaceRecentSchema.parse(row)) : [],
    }));
  },

  getTodayWorkspace(): Promise<OmWorkspaceTodayData> {
    return api.get<OmWorkspaceTodayData>('/work-surface/today');
  },
};
